/**
 * De papieren toets voor één leerling (stap 24): van kruk naar trap.
 *
 * In de app leest de leerling met de hulpmiddelen van Anna Maria (de kruk).
 * Of hij een tekst een stap hoger ook zonder die hulp kan lezen, laat een
 * toets op papier zien. De volgorde (stap 24b): de leerkracht kiest een tekst
 * en een niveau (standaard een stap hoger dan nu), de app maakt de vragen, de
 * leerkracht downloadt de toets en apart het nakijkblad als pdf, en de
 * leerling maakt hem op papier. Daarna kijken ze hem samen na en beslissen
 * ze samen; dat besluit legt de leerkracht hier vast: "Samen besloten: naar
 * E5" (de leerling leest voortaan op dat niveau, zoals in Groepen) of "Nog
 * niet". Het is geen examen: de app legt geen score vast, alleen het besluit,
 * en dat staat in de ontwikkeling.
 *
 * Teksten die de leerling al in de app las (of al op papier kreeg), staan
 * achteraan, met een opmerking: met een nieuwe tekst zegt de toets het meest.
 */
import React, { useEffect, useState } from 'react';
import { Download, FileText, Trash2 } from 'lucide-react';
import { leerkrachtApi as api } from '../../../api/leerkracht';
import { Draaier, Laden } from '../../../components/Laden';
import { Venster } from '../../../components/Venster';
import { AANTALLEN_MEERKEUZE, AANTALLEN_OPEN, NIVEAUS } from '../../../keuzelijsten';

/** Een stap hoger dan `niveau` (bij het hoogste, of een niveau buiten de lijst: hetzelfde). */
export const stapHoger = (niveau) => {
  const plek = NIVEAUS.indexOf(niveau);
  return plek >= 0 && plek + 1 < NIVEAUS.length ? NIVEAUS[plek + 1] : niveau;
};
const isHoger = (niveau, dan) => NIVEAUS.indexOf(niveau) > NIVEAUS.indexOf(dan);

/** "2026-10-28T19:40:00" → "28-10-2026". */
const datum = (iso) => (iso ? iso.slice(0, 10).split('-').reverse().join('-') : '');
const kaal = (tekst) => (tekst || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);

/** Een blob als bestand laten downloaden. */
function bewaar(blob, naam) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = naam;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL?.(url), 1000);
}

export function PapierenToets({ leerling, onSluit, meldFout, onNieuwNiveau = () => {} }) {
  const [data, setData] = useState(null);
  const [keuze, setKeuze] = useState({ text_id: '', niveau: '', meerkeuze: 5, open: 2 });
  const [maken, setMaken] = useState(false);
  const [melding, setMelding] = useState('');
  const [laadt, setLaadt] = useState(null);   // welke pdf wordt gehaald
  const [besluiten, setBesluiten] = useState(null);   // de toets waarvan het besluit onderweg is

  const laad = async () => {
    try {
      const uit = await api.haalPapierenToetsen(leerling.id);
      setData(uit);
      setKeuze(k => ({ ...k, niveau: k.niveau || stapHoger(uit.niveau) }));   // de toets komt vóór het besluit
    } catch (err) {
      meldFout(err.message);
    }
  };

  useEffect(() => {
    laad();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen
  }, [leerling.id]);

  const zet = (veld, getal = false) => (e) => setKeuze({ ...keuze, [veld]: getal ? parseInt(e.target.value) : e.target.value });

  const maak = async () => {
    setMaken(true);
    setMelding('');
    try {
      const toets = await api.maakPapierenToets(leerling.id, keuze);
      setMelding(`De toets "${toets.titel}" op ${toets.niveau} is klaar. Download hem hieronder.`);
      setKeuze(k => ({ ...k, text_id: '' }));
      await laad();
    } catch (err) {
      meldFout(err.name === 'AbortError' ? 'Het maken duurde te lang. Probeer het opnieuw.' : err.message);
    } finally {
      setMaken(false);
    }
  };

  const download = async (toets, nakijkblad) => {
    setLaadt(`${toets.id}-${nakijkblad}`);
    try {
      const blob = await api.haalPapierenToetsPdf(toets.id, nakijkblad);
      bewaar(blob, `${nakijkblad ? 'nakijkblad' : 'toets'}-${kaal(leerling.naam)}-${kaal(toets.titel)}.pdf`);
    } catch (err) {
      meldFout(err.message);
    } finally {
      setLaadt(null);
    }
  };

  /** Wat leerkracht en leerling samen besloten na het nakijken: 'hoger' of 'blijft'. */
  const besluit = async (toets, wat) => {
    setBesluiten(toets.id);
    setMelding('');
    try {
      const uit = await api.legBesluitVast(toets.id, wat);
      setMelding(wat === 'hoger' ? `${leerling.naam} leest voortaan op ${toets.niveau}.`
        : `Vastgelegd: ${leerling.naam} blijft nog op ${data.niveau}.`);
      if (uit.niveau) {
        onNieuwNiveau(uit.niveau);
        setKeuze(k => ({ ...k, niveau: '' }));   // een volgende toets: weer een stap hoger dan het nieuwe niveau
      }
      await laad();
    } catch (err) {
      meldFout(err.message);
    } finally {
      setBesluiten(null);
    }
  };

  const verwijder = async (toets) => {
    if (!window.confirm(`De papieren toets "${toets.titel}" van ${datum(toets.datum)} verwijderen?`)) return;
    try {
      await api.verwijderPapierenToets(toets.id);
      await laad();
    } catch (err) {
      meldFout(err.message);
    }
  };

  return (
    <Venster breed={820} vanBoven>
      <div className="flex-between mb-8">
        <h2><FileText size={20} style={{ verticalAlign: 'text-bottom' }} /> Papieren toets voor {leerling.naam}</h2>
        <button className="btn btn-secondary" onClick={onSluit}>Sluiten</button>
      </div>
      <p className="text-muted text-small mb-16">
        Van kruk naar trap: {leerling.naam} maakt deze toets op papier, zonder de hulpmiddelen van Anna Maria. Daarna kijkt
        u hem samen na, met het nakijkblad, en beslist u samen of {leerling.naam} een stap hoger gaat. Leg dat besluit
        hieronder vast bij de toets; het komt in de ontwikkeling. Het is geen examen: de app bewaart geen score.
      </p>
      {!data ? <Laden tekst="Laden..." /> : (
        <>
          {data.teksten.length === 0 ? (
            <div className="alert mb-16" style={{ background: 'var(--waarschuw-vlak)', color: 'var(--waarschuw-tekst)' }}>
              Er is nog geen tekst die op elk niveau is goedgekeurd.
            </div>
          ) : (
            <div className="card mb-16" style={{ background: 'var(--vlak)' }}>
              <div className="grid-2">
                <div className="form-group">
                  <label htmlFor="pt-tekst">Tekst</label>
                  <select id="pt-tekst" className="input" value={keuze.text_id} onChange={zet('text_id')}>
                    <option value="">Kies een tekst</option>
                    {data.teksten.map(t => (
                      <option key={t.id} value={t.id}>{t.titel}{t.gelezen ? ` (al gelezen door ${leerling.naam})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="pt-niveau">Niveau</label>
                  <select id="pt-niveau" className="input" value={keuze.niveau} onChange={zet('niveau')}>
                    {!NIVEAUS.includes(keuze.niveau) && keuze.niveau && <option value={keuze.niveau}>{keuze.niveau}</option>}
                    {NIVEAUS.map(n => (
                      <option key={n} value={n}>
                        {n}{n === data.niveau ? ` (niveau van ${leerling.naam} nu)` : ''}
                        {n !== data.niveau && n === stapHoger(data.niveau) ? ' (een stap hoger)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="pt-mc">Meerkeuzevragen</label>
                  <select id="pt-mc" className="input" value={keuze.meerkeuze} onChange={zet('meerkeuze', true)}>
                    {AANTALLEN_MEERKEUZE.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="pt-open">Open vragen</label>
                  <select id="pt-open" className="input" value={keuze.open} onChange={zet('open', true)}>
                    {AANTALLEN_OPEN.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
              {data.teksten.find(t => t.id === keuze.text_id)?.gelezen && (
                <p className="text-small mb-8" style={{ color: 'var(--waarschuw-tekst)' }}>
                  {leerling.naam} las deze tekst al. Met een tekst die nieuw is, zegt de toets meer.
                </p>
              )}
              <button className="btn btn-primary" onClick={maak}
                disabled={maken || !keuze.text_id || keuze.meerkeuze + keuze.open === 0}>
                {maken ? <><Draaier /> De vragen worden gemaakt...</> : <><FileText size={16} /> Maak de toets</>}
              </button>
              {maken && <span className="text-muted text-small" style={{ marginLeft: 8 }}>Dit kan een minuut duren.</span>}
            </div>
          )}
          {melding && <div className="alert alert-success mb-16">{melding}</div>}

          <h3 className="mb-8">Gemaakte toetsen</h3>
          {data.toetsen.length === 0 ? <p className="text-muted">Nog geen papieren toetsen.</p> : (
            <table style={{ fontSize: 13 }} data-testid="papieren-toetsen">
              <tbody>
                {data.toetsen.map(t => (
                  <React.Fragment key={t.id}>
                  <tr style={{ borderBottom: 'none' }}>
                    <td style={{ whiteSpace: 'nowrap' }}>{datum(t.datum)}</td>
                    <td>{t.titel}</td>
                    <td><span className="badge badge-primary">{t.niveau}</span></td>
                    <td className="text-muted">{t.meerkeuze} meerkeuze, {t.open} open</td>
                    <td>
                      <div className="flex gap-4">
                        <button className="btn btn-primary" style={{ padding: '4px 8px', fontSize: 12 }}
                          onClick={() => download(t, false)} disabled={!!laadt}>
                          {laadt === `${t.id}-false` ? <Draaier /> : <Download size={12} />} Toets (pdf)
                        </button>
                        <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: 12 }}
                          onClick={() => download(t, true)} disabled={!!laadt}>
                          {laadt === `${t.id}-true` ? <Draaier /> : <Download size={12} />} Nakijkblad (pdf)
                        </button>
                        <button className="btn btn-danger" style={{ padding: '4px 8px' }} onClick={() => verwijder(t)}
                          aria-label={`Verwijder de toets ${t.titel}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr data-testid={`besluit-${t.id}`}>
                    <td></td>
                    <td colSpan={4} style={{ paddingTop: 0 }}>
                      <Besluit toets={t} naam={leerling.naam} nu={data.niveau} bezig={besluiten === t.id}
                        onBesluit={(wat) => besluit(t, wat)} />
                    </td>
                  </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </Venster>
  );
}

/**
 * Na het samen nakijken: het besluit, of de knoppen om het vast te leggen.
 * "Naar E5" alleen als de toets hoger is dan het niveau van nu; na "nog
 * niet" kan het later nog "naar E5" worden, na "naar E5" niet meer.
 */
function Besluit({ toets, naam, nu, bezig, onBesluit }) {
  const knop = { padding: '2px 8px', fontSize: 12 };
  const kanHoger = toets.besluit !== 'hoger' && isHoger(toets.niveau, nu);
  return (
    <div className="flex gap-8" style={{ alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
      {toets.besluit === 'hoger' && (
        <span className="badge badge-success">Samen besloten op {datum(toets.besloten_op)}: naar {toets.niveau}</span>
      )}
      {toets.besluit === 'blijft' && (
        <span className="badge badge-warning">
          Samen besloten op {datum(toets.besloten_op)}: nog op {toets.niveau_toen || nu}
        </span>
      )}
      {!toets.besluit && <span className="text-muted">Na het samen nakijken:</span>}
      {kanHoger && (
        <button className="btn btn-success" style={knop} disabled={bezig} onClick={() => onBesluit('hoger')}>
          {bezig ? <Draaier /> : null} {toets.besluit === 'blijft' ? 'Toch' : 'Samen besloten:'} naar {toets.niveau}
        </button>
      )}
      {!toets.besluit && (
        <button className="btn btn-secondary" style={knop} disabled={bezig} onClick={() => onBesluit('blijft')}>
          Nog niet: {naam} blijft op {nu}
        </button>
      )}
    </div>
  );
}
